<template>
  <div>
    <div class="flex relative mx-auto mb-4" style="max-width: 960px">
      <div class="pl-20 pr-10 box-border" style="width: 240px">
        <ul class="fixed text-gray-62 text-sm">
          <li
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 1,
            }"
            @click="scrollTo('#serviceDetail1')"
          >
            基本情報
          </li>
          <li
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 2,
            }"
            @click="scrollTo('#serviceDetail2')"
          >
            参考価格
          </li>
          <li
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 3,
            }"
            @click="scrollTo('#serviceDetail3')"
          >
            特長やオプション
          </li>
          <li
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 4,
            }"
            @click="scrollTo('#serviceDetail4')"
          >
            詳細
          </li>
          <li
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 5,
            }"
            @click="scrollTo('#serviceDetail5')"
          >
            実績
          </li>
        </ul>
      </div>

      <div>
        <section class="pr-20" style="max-width: 720px">
          <h3 id="serviceDetail1" class="align-baseline mb-8">
            <span class="text-gray-92 font-semibold text-2xl mr-2">{{
              detail.category_name
            }}</span>
            <span class="text-gray-62 font-light text-sm">{{
              detail.category_class_name
            }}</span>
          </h3>

          <div class="flex justify-between items-start mb-5">
            <div
              :style="{
                width: detail.is_logo_image_displayed
                  ? 'calc(100% - 178px)'
                  : 'auto',
              }"
            >
              <span
                class="block text-gray-92 font-light text-lg leading-7 mb-8"
              >
                {{ detail.catchphrase }}
              </span>
              <a
                :href="detail.url"
                target="_blank"
                class="text-gray-62 font-light text-sm"
              >
                {{ detail.url }}
              </a>
            </div>
            <div
              v-if="detail.is_logo_image_displayed"
              class="flex items-center justify-center px-4 py-3 ml-4 border box-border"
              style="width: 162px; height: 116px"
            >
              <img
                class="max-w-full max-h-full m-auto"
                :src="detail.logo_image_file"
              />
            </div>
          </div>

          <div class="mb-16">
            <ImageSlider
              :images="detail.images"
              :width="640"
              :height="460"
              :spaceBetween="40"
              :useNavigation="false"
              :useThumb="true"
            />
          </div>

          <div class="mb-16">
            <h4 class="block text-gray-92 font-semibold text-xl leading-8 mb-8">
              サービスの強み
            </h4>
            <div
              v-for="(item, key) in detail.advantages"
              :key="'advantage' + key"
              class="flex items-center mb-8"
            >
              <img class="w-5 mr-4" src="@/assets/icons/CheckCoral.svg" />
              <span
                class="text-gray-92 text-sm font-semibold whitespace-pre-wrap"
                >{{ item.value }}</span
              >
            </div>
          </div>
          <div class="mb-16">
            <h4
              id="serviceDetail2"
              class="block text-gray-92 font-semibold text-xl leading-8 mb-8"
            >
              参考価格
            </h4>
            <div>
              <div
                v-for="(item, key) in detail.prices"
                :key="'prive' + key"
                class="flex items-center justify-between pb-4 mb-4 border-b"
              >
                <div>
                  <span class="block text-gray-92 text-sm font-semibold mb-2">{{
                    item.title
                  }}</span>
                  <span class="block text-gray-62 text-sm font-light">
                    {{ item.description }}
                  </span>
                </div>
                <div
                  class="align-baseline text-gray-92 font-semibold ml-4 whitespace-no-wrap"
                >
                  <span class="text-3xl mr-1">
                    {{
                      item.price && item.price.value
                        ? amountFormatter(item.price.value)
                        : ''
                    }}
                  </span>
                  <span class="text-sm">{{
                    item.price && item.price.unit ? item.price.unit : ''
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-16">
            <h4
              id="serviceDetail3"
              class="block text-gray-92 font-semibold text-xl leading-8 mb-8"
            >
              サービスの特徴
            </h4>
            <div
              v-for="(feature, key) in detail.features"
              :key="`feature${key}`"
              style="margin-bottom: 30px"
            >
              <span class="block text-gray-92 text-sm mb-2">{{
                feature.name
              }}</span>
              <div class="flex flex-wrap">
                <span
                  v-for="(item, key2) in feature.values"
                  :key="`feature${key}_${key2}`"
                  class="text-gray-92 text-xs font-light text-center leading-8 mr-1 mb-1"
                  style="
                    background: #ffe9e9;
                    padding: 0 10px;
                    border-radius: 3px;
                  "
                >
                  {{ item.value }}
                </span>
              </div>
            </div>
          </div>

          <div class="mb-16">
            <h4 class="block text-gray-92 font-semibold text-xl leading-8 mb-8">
              対応可能なオプション
            </h4>
            <div
              v-for="(item, key) in detail.options"
              :key="`option${key}`"
              class="mb-12"
            >
              <span class="block text-gray-92 font-semibold text-sm mb-4">{{
                item.name
              }}</span>
              <div class="flex items-center justify-between flex-wrap">
                <div
                  v-for="(value, key2) in item.values"
                  :key="`option${key}_${key2}`"
                  class="flex items-center justify-between border-b border-gray-12 py-2"
                  :class="{ 'border-t': key2 < 2 }"
                  style="width: calc(50% - 20px); min-height: 48px"
                >
                  <span class="text-gray-92 text-xs font-light leading-4">
                    {{ value.value }}
                  </span>
                  <img
                    v-if="value.is_available"
                    class="w-6 mx-2"
                    src="@/assets/icons/True.svg"
                  />
                  <img
                    v-else
                    class="w-5 mx-2 box-content"
                    style="padding: 0 2px"
                    src="@/assets/icons/Exit.svg"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="mb-16">
            <h4
              id="serviceDetail4"
              class="block text-gray-92 font-semibold text-xl leading-8 mb-6"
            >
              サービス詳細
            </h4>
            <div>
              <div
                v-for="(item, key) in detail.items"
                v-show="hasValue(item.values)"
                :key="`item${key}`"
                class="flex items-center border-b"
                style="min-height: 56px; padding: 18px 8px"
              >
                <span class="text-gray-62 text-sm w-4/12 mr-4 font-semibold">{{
                  item.name
                }}</span>
                <div class="text-gray-92 text-sm leading-5 w-8/12">
                  <span
                    v-for="(value, key2) in item.values"
                    :key="`item${key}_${key2}`"
                    class="whitespace-pre-wrap"
                  >
                    {{ value.value + (item.unit || '')
                    }}{{ item.values.length - 1 != key2 ? ' / ' : '' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-16">
            <h4
              id="serviceDetail5"
              class="block text-gray-92 font-semibold text-xl leading-8 mb-8"
            >
              実績
            </h4>
            <div class="px-10 border divide-y divide-gray-12">
              <div v-for="(item, key) in detail.cases" :key="'case' + key">
                <div v-if="detail.case_image_exists" class="py-12">
                  <div
                    class="relative flex items-center justify-center w-full mb-4 bg-white"
                    style="height: 400px"
                  >
                    <img
                      class="max-w-full max-h-full m-auto"
                      :src="item.image_file"
                    />
                    <div v-if="item.url" class="text-right mb-6 z-20">
                      <a
                        :href="item.url"
                        target="_blank"
                        class="absolute flex justify-center items-center bg-white rounded-full w-8 h-8"
                        style="
                          bottom: 13px;
                          right: 13px;
                          box-shadow: 0px 0px 8px rgba(152, 163, 190, 0.6);
                        "
                      >
                        <img
                          src="@/assets/icons/Open.svg"
                          style="width: 18px"
                        />
                      </a>
                    </div>
                  </div>
                  <span
                    class="block text-xl leading-8 font-semibold mb-2"
                    style="color: #34464"
                  >
                    {{ item.title }}
                  </span>
                  <span class="block text-gray-92 text-sm font-light mb-6">{{
                    item.customer
                  }}</span>
                  <div class="flex items-center justify-between mb-4">
                    <div
                      class="flex items-center text-gray-92 text-xs font-light"
                    >
                      <span
                        class="leading-7 bg-slate-50 mr-2"
                        style="padding: 0 10px"
                      >
                        {{ item.project_term }}
                      </span>
                    </div>
                    <div
                      class="text-gray-92 text-xs align-baseline font-semibold whitespace-no-wrap"
                    >
                      <span>費用目安</span>
                      <span class="text-lg mx-1">
                        {{
                          item.price && item.price.value
                            ? amountFormatter(item.price.value)
                            : ''
                        }}
                      </span>
                      <span>{{
                        item.price && item.price.unit ? item.price.unit : ''
                      }}</span>
                    </div>
                  </div>
                  <div>
                    <span
                      class="text-gray-92 text-sm font-light leading-7 whitespace-pre-wrap"
                    >
                      {{ item.description }}
                    </span>
                  </div>
                </div>

                <div v-if="!detail.case_image_exists" class="py-12">
                  <span
                    class="block text-xl font-semibold mb-2"
                    style="color: #34464"
                  >
                    {{ item.title }}
                  </span>
                  <span
                    class="block text-gray-62 text-sm font-light"
                    style="margin-bottom: 28px"
                  >
                    {{ item.customer }}
                  </span>
                  <span class="block text-gray-92 text-xm font-light mb-4">
                    {{ item.introduction_time }}
                  </span>
                  <div>
                    <span class="text-gray-62 text-sm font-light leading-7">
                      {{ item.description }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, onMounted } from 'vue';
import { amountFormatter } from '@/utils/filters.js';
import ImageSlider from '@/components/modalRight/service/ImageSlider.vue';

export default {
  props: {
    detail: Object,
  },
  components: {
    ImageSlider,
  },
  setup() {
    const state = reactive({
      visibleSection: 1,
    });
    const hasValue = (values) => {
      const input = values.find((e) => e.value !== null && e.value !== '');
      return input ? true : false;
    };
    const scrollTo = (id) => {
      const modal = document.getElementById('modalRight');
      const target = document.getElementById(id.replace('#', ''));
      modal.scrollTop = target.offsetTop;
    };
    onMounted(() => {
      const modal = document.getElementById('modalRight');
      const target = [
        document.getElementById('serviceDetail1'),
        document.getElementById('serviceDetail2'),
        document.getElementById('serviceDetail3'),
        document.getElementById('serviceDetail4'),
        document.getElementById('serviceDetail5'),
      ];
      modal.onscroll = () => {
        const scrollTop = modal.scrollTop;
        for (let i = target.length; i > 0; i--) {
          if (target[i - 1] && scrollTop >= target[i - 1].offsetTop) {
            state.visibleSection = i;
            break;
          }
        }
      };
    });
    return {
      state,
      hasValue,
      scrollTo,
      amountFormatter,
    };
  },
};
</script>

<style lang="scss" scoped>
.card-shadow:hover {
  box-shadow: 0px 0px 8px rgba(102, 113, 140, 0.3);
}
</style>
